/**
 * ============================================================================
 * THEME RENDERERS - Reusable Theme Display Components
 * ============================================================================
 * 
 * PURPOSE:
 * Provides reusable rendering functions for wedding theme displays across
 * DreamWeddingGenerator and MoodboardMatcher. Ensures consistent UI/UX
 * and reduces code duplication.
 * 
 * KEY RENDERING FUNCTIONS:
 * 
 * 1. renderThemeHeader(theme, modelName)
 *    - Renders theme title and description
 *    - Shows AI model badge (Gemini/Claude/OpenAI)
 *    - Displays theme mood and style
 * 
 * 2. renderColorPalette(theme, onColorClick)
 *    - Displays color palette with interactive color cards
 *    - Shows color names and hex codes
 * 
 * 3. renderRitualSection(rituals, themeName, setRitualModal)
 *    - Displays suggested cultural rituals
 *    - Shows ritual cards with similarity scores
 *    - Opens modal on click
 *    - Triggers AI Color Magic on click
 * 
 * 3. renderStyleKeywords(theme)
 *    - Displays style tags with icons (romantic, rustic, modern, etc.)
 *    - Visual style indicators
 * 
 * 4. renderElements(theme)
 *    - Renders wedding elements (flowers, venues, decor, etc.)
 *    - Organized by category
 * 
 * 5. renderSaveButton(themeData, modelName, saveCallback, saving, vendorBanner)
 *    - Save theme button with model-specific styling
 *    - Shows vendor banner after save
 *    - Handles loading state
 * 
 * 6. renderColorOrbs(colors)
 *    - Animated floating color orbs background
 *    - Creates immersive color experience
 * 
 * 7. renderColorCodes(colors)
 *    - Displays hex codes in elegant format
 *    - Copy-to-clipboard functionality
 * 
 * 8. renderColorFlow(colors)
 *    - Animated gradient flow effect
 *    - Smooth color transitions
 * 
 * 9. renderAnimatedBackground(colors)
 *    - Full animated background with color orbs
 *    - Used in theme display views
 * 
 * DESIGN PRINCIPLES:
 * - Consistent styling across all pages
 * - Smooth animations and transitions
 * - Responsive design
 * - Accessibility compliant
 * - Model-specific color schemes (Gemini blue, Claude orange, OpenAI green)
 * 
 * USAGE:
 * - DreamWeddingGenerator: All theme displays
 * - MoodboardMatcher: All theme displays
 * - Compare mode: Side-by-side theme comparison
 * 
 * ============================================================================
 */

// Theme Rendering Helper Functions
// Extracted from Dream Wedding Generator for reusable rendering logic
// Part 1: Save Button, Elements, Style Keywords
// Part 2: Color Orbs, Color Codes, Color Flow

import React from 'react';
import { getStyleIcon } from './styleHelpers';
import { parseColors, extractHexFromColor, getContrastColor, getColorName } from './colorHelpers';
import { apiService } from '../services/apiService';

// Helper function to render save button
export const renderSaveButton = (themeData, modelName, saveIndividualTheme, saving, showVendorBanner, setShowVendorBanner, timeTracker = null, onNotification = null) => {
  
  const handleVendorMatchingClick = async (e) => {
    e.preventDefault();
    
    // Check if theme has been saved (has ID)
    if (!themeData.id) {
      if (onNotification) {
        onNotification({
          show: true,
          title: 'Save Required',
          message: '💫 Please save your theme first before finding vendors!',
          type: 'warning'
        });
      }
      return;
    }
    
    // Determine source page
    const from = modelName === 'Dream Wedding' ? 'dream-generator' : 
                 modelName === 'Moodboard' ? 'moodboard-matcher' : 'theme-page';

    // Track vendor navigation intent (once per session)
    const vendorNavKey = `vendor_nav_tracked_${themeData.id}`;
    if (!sessionStorage.getItem(vendorNavKey)) {
      sessionStorage.setItem(vendorNavKey, '1');
      try {
        await apiService.feedback.trackVendorNavigation({
          theme_id: themeData.id,
          entry_point: from
        });
      } catch (error) {
        console.error('Error tracking vendor navigation:', error);
      }
    }
    
    // Extract ritual IDs if available
    const ritualIds = themeData.suggested_rituals?.map(r => r.ritual_key) || [];
    
    // Serialize state to URL params for new tab
    const params = new URLSearchParams({
      theme_id: themeData.id,
      ritual_ids: JSON.stringify(ritualIds),
      from: from
    });
    
    window.open(`/vendor-vault/smart-vendor-matching?${params.toString()}`, '_blank');
  };
  
  const getModelColors = (model) => {
    switch (model.toLowerCase()) {
      case 'claude':
        return {
          gradient: 'from-orange-400 via-orange-500 to-orange-600',
          hoverGradient: 'hover:from-orange-500 hover:via-orange-600 hover:to-orange-700',
          icon: '🎨'
        };
      case 'openai':
        return {
          gradient: 'from-blue-400 via-blue-500 to-green-500',
          hoverGradient: 'hover:from-blue-500 hover:via-blue-600 hover:to-green-600',
          icon: '🤖'
        };
      case 'gemini':
        return {
          gradient: 'from-blue-500 via-purple-500 to-indigo-500',
          hoverGradient: 'hover:from-blue-600 hover:via-purple-600 hover:to-indigo-600',
          icon: '✨'
        };
      default:
        return {
          gradient: 'from-gray-400 via-gray-500 to-gray-600',
          hoverGradient: 'hover:from-gray-500 hover:via-gray-600 hover:to-gray-700',
          icon: '🎨'
        };
    }
  };

  // Get color scheme for selected model
  const colors = getModelColors(modelName);
  
  // ========================================================================
  // HANDLE SAVE FUNCTION POLYMORPHISM
  // ========================================================================
  // Different pages use different save function signatures:
  // - DreamWeddingGenerator: saveTheme() - no params, uses state
  // - MoodboardMatcher compare mode: saveIndividualTheme(themeData, modelName)
  // 
  // Detect which signature by checking function.length (parameter count)
  const handleSave = () => {
    if (saveIndividualTheme.length === 0) {
      // No parameters - it's saveTheme() that uses component state
      saveIndividualTheme();
    } else {
      // Has parameters - it's saveIndividualTheme(themeData, modelName)
      // Used in compare mode to save specific model's theme
      saveIndividualTheme(themeData, modelName);
    }
  };
  
  return (
    <div className="text-center mt-6">
      {/* ====================================================================
          SAVE BUTTON - VowAI Magic Styling (hidden after save)
          ==================================================================== */}
      {!themeData.id && (
      <button
        onClick={handleSave}
        disabled={saving}
        className="relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none animate-pulse group"
      >
        {/* Animated heart icon */}
        <span className="mr-3 text-xl group-hover:animate-bounce" style={{ animationDelay: '0.1s' }}>💖</span>
        
        {/* Button text - changes based on saving state */}
        {saving ? 'Saving...' : `Save ${modelName} Theme`}
        
        {/* Animated sparkle icon */}
        <span className="ml-3 text-xl group-hover:animate-bounce" style={{ animationDelay: '0.2s' }}>✨</span>
        
        {/* VowAI signature glow effect on hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
      </button>
      )}

      {/* ====================================================================
          LOGIN PROMPT - Show if user not authenticated
          ==================================================================== */}
      {!themeData.id && !localStorage.getItem('token') && (
        <p className="text-sm text-gray-600 mt-2">
          Please log in to save themes to your dashboard
        </p>
      )}

      {/* ====================================================================
          SMART VENDOR MATCHING DISCOVERY BANNER
          ==================================================================== 
          Shows after successful theme save to promote next step in workflow
          Encourages users to find vendors matching their theme */}
      {showVendorBanner && (
        <div className="mt-8 animate-fadeInUp">
          <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-emerald-50 border-2 border-purple-200/50 rounded-3xl p-6 shadow-xl hover:shadow-2xl transition-all duration-500 relative overflow-hidden">
            {/* Dismiss button - allows user to hide banner */}
            <button
              onClick={() => setShowVendorBanner(false)}
              className="absolute top-3 right-3 w-8 h-8 bg-white/70 hover:bg-white/90 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all duration-300 hover:scale-110"
            >
              <span className="text-sm">✕</span>
            </button>

            {/* Magical background sparkles - decorative animation */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-2 left-4 text-purple-400 animate-bounce delay-100">✨</div>
              <div className="absolute top-4 right-6 text-blue-400 animate-bounce delay-300">🔮</div>
              <div className="absolute bottom-3 left-1/3 text-emerald-400 animate-bounce delay-500">💫</div>
            </div>

            {/* Banner content */}
            <div className="relative z-10 text-center">
              {/* Banner title with animated icons */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-3xl animate-pulse">🎯</span>
                <h4 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent">
                  Ready to Bring Your Theme to Life?
                </h4>
                <span className="text-3xl animate-pulse">✨</span>
              </div>
              
              {/* Banner description */}
              <p className="text-gray-700 mb-6 leading-relaxed">
                Your beautiful theme is saved! Now discover <span className="font-bold text-purple-600">AI-matched vendors</span> who specialize in your exact style, colors, and vision.
              </p>
              
              {/* Call-to-action button - navigates to Smart Vendor Matching */}
              <button
                onClick={handleVendorMatchingClick}
                className="group inline-flex items-center bg-gradient-to-r from-purple-500 via-blue-500 to-emerald-500 text-white py-3 px-8 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
              >
                <span className="mr-3 text-xl group-hover:animate-bounce">🔍</span>
                Discover Smart Vendor Matches
                <span className="ml-3 text-xl group-hover:animate-bounce">🎪</span>
              </button>
              
              <p className="text-sm text-gray-600 mt-3 italic">
                AI-powered matching based on your saved theme{themeData.suggested_rituals?.length > 0 ? ' + rituals' : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// WEDDING ELEMENTS RENDERER
// ============================================================================

/**
 * Render wedding elements (flowers, venues, decor, etc.)
 * 
 * WHAT IT DOES:
 * 1. Displays key wedding elements from AI-generated theme
 * 2. Shows items like: "Garden roses", "Rustic barn venue", "String lights"
 * 3. Adapts layout based on display mode
 * 4. Adds hover effects and visual polish
 * 
 * ELEMENT TYPES:
 * - Flowers: Specific flower types and arrangements
 * - Venues: Venue styles and locations
 * - Decor: Decorative elements and details
 * - Attire: Dress styles, suit colors
 * - Other: Any wedding-related items
 * 
 * DISPLAY MODES:
 * - Individual: 2-column grid, larger cards, gold/yellow theme
 * - Compare: Simple list, compact, emerald/green theme
 * 
 * VISUAL DESIGN:
 * - Individual: Gold gradient cards with dot indicators
 * - Compare: White list items with emerald bullets
 * 
 * @param {Array} elements - Wedding elements array
 * @param {String} style - Display style: 'individual' or 'compare'
 * @returns {JSX.Element|null} Elements display or null if no elements
 */
export const renderElements = (elements, style = 'compare') => {
  // Validation: return null if no elements to display
  if (!elements || elements.length === 0) return null;

  // ========================================================================
  // INDIVIDUAL MODE: Fancy grid layout
  // ========================================================================
  if (style === 'individual') {
    return (
      <div className="group">
        {/* Section title with animated flower icon */}
        <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
          <span className="mr-3 text-3xl animate-bounce">🌸</span> Wedding Elements
        </h3>
        
        {/* 2-column grid layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {elements.map((element, index) => (
            <div key={index} className="group/element transform hover:scale-105 transition-all duration-300">
              {/* Element card with gold gradient */}
              <div className="bg-gradient-to-r from-gold-100 to-yellow-100 p-4 rounded-2xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-gold-200/20 to-yellow-200/20 opacity-0 group-hover/element:opacity-100 transition-opacity duration-300"></div>
                
                {/* Element content with dot indicator */}
                <div className="relative z-10 flex items-center">
                  <div className="w-3 h-3 bg-gradient-to-br from-gold-400 to-yellow-500 rounded-full mr-3 shadow-sm"></div>
                  <span className="text-gold-700 font-bold">{element}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  } 
  // ========================================================================
  // COMPARE MODE: Simple list layout
  // ========================================================================
  else {
    return (
      <div className="bg-gradient-to-br from-emerald-50/90 to-mint-100/90 rounded-xl p-4 border border-emerald-200/50 group">
        {/* Section title with leaf icon */}
        <div className="flex items-center mb-3">
          <span className="text-2xl mr-2">🌿</span>
          <h4 className="text-lg font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-emerald-600 group-hover:to-green-600 group-hover:bg-clip-text transition-all duration-300">Key Elements</h4>
        </div>
        
        {/* Simple list with bullets */}
        <ul className="space-y-2">
          {elements.map((element, index) => (
            <li key={index} className="text-gray-700 flex items-center bg-white/60 p-2 rounded-lg border border-emerald-200/30">
              <span className="text-emerald-500 mr-2 text-sm">•</span>
              <span className="text-sm break-words whitespace-normal">{element}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
};

// ============================================================================
// STYLE KEYWORDS RENDERER
// ============================================================================

/**
 * Render style keywords (romantic, rustic, modern, etc.)
 * 
 * WHAT IT DOES:
 * 1. Displays wedding style tags
 * 2. Shows icons for each style (individual mode)
 * 3. Capitalizes keywords for consistency
 * 4. Adds hover effects (scale, rotate, shadow)
 * 5. Adapts layout based on display mode
 * 
 * STYLE KEYWORDS:
 * - romantic, elegant, rustic, outdoor, vintage, modern, classic, bohemian
 * - formal, traditional, garden, beach, luxurious, intimate, whimsical
 * - cultural, colorful, artistic, natural, sophisticated
 * 
 * DISPLAY MODES:
 * - Individual: Large pills with icons, scale/rotate on hover
 * - Compare: Compact rounded tags, simple hover shadow
 * 
 * VISUAL DESIGN:
 * - Individual: Purple/indigo gradient, larger text, icons
 * - Compare: Purple/pink gradient, smaller text, no icons
 * 
 * @param {Array} keywords - Style keywords array
 * @param {String} style - Display style: 'individual' or 'compare'
 * @returns {JSX.Element|null} Style keywords display or null if no keywords
 */
export const renderStyleKeywords = (keywords, style = 'compare') => {
  // Validation: return null if no keywords to display
  if (!keywords || keywords.length === 0) return null;

  // ========================================================================
  // INDIVIDUAL MODE: Fancy layout with icons
  // ========================================================================
  if (style === 'individual') {
    return (
      <div className="group">
        {/* Section title with animated sparkle icon */}
        <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-indigo-600 group-hover:bg-clip-text transition-all duration-300">
          <span className="mr-3 text-3xl animate-bounce">✨</span> Style Keywords
        </h3>
        
        {/* Keywords grid with hover effects */}
        <div className="flex flex-wrap gap-4">
          {keywords.map((keyword, index) => (
            <div key={index} className="transform hover:scale-110 hover:rotate-2 transition-all duration-300">
              {/* Large pill with icon and text */}
              <span className="px-6 py-3 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-2xl font-bold border-2 border-purple-200/50 shadow-lg hover:shadow-xl transition-all duration-300 inline-block">
                <span className="mr-2 text-lg">{getStyleIcon(keyword)}</span>  {/* Icon from styleHelpers */}
                <span className="capitalize">{keyword}</span>                   {/* Capitalized keyword */}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  } 
  // ========================================================================
  // COMPARE MODE: Simple rounded tags
  // ========================================================================
  else {
    return (
      <div className="bg-gradient-to-br from-purple-50/90 to-lavender-100/90 rounded-xl p-4 border border-purple-200/50 group">
        {/* Section title with sparkle icon */}
        <div className="flex items-center mb-3">
          <span className="text-2xl mr-2">💫</span>
          <h4 className="text-lg font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-indigo-600 group-hover:bg-clip-text transition-all duration-300">Style Keywords</h4>
        </div>
        
        {/* Compact keyword tags */}
        <div className="flex flex-wrap gap-2">
          {keywords.map((keyword, index) => (
            <span key={index} className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-semibold border border-purple-200 hover:shadow-md transition-all duration-300 capitalize">
              {keyword}
            </span>
          ))}
        </div>
      </div>
    );
  }
};

// ============================================================================
// COLOR ORBS RENDERER (Interactive Color Circles)
// ============================================================================

/**
 * Render interactive color orbs (clickable circles)
 * 
 * WHAT IT DOES:
 * 1. Displays each color as a beautiful 3D orb
 * 2. Adds hover animations (scale, rotate)
 * 3. Shows color name below each orb
 * 4. Opens AI Color Magic modal on click
 * 5. Includes visual effects (glow, shine, ping animation)
 * 
 * VISUAL EFFECTS:
 * - Outer glow: Animated ping effect (staggered by index)
 * - Main orb: 3D appearance with shadow and border
 * - Inner shine: White highlight (top-left) and dark shadow (bottom-right)
 * - Hover: Scale up 125%, rotate 12 degrees
 * 
 * CLICK BEHAVIOR:
 * - Extracts hex code from color
 * - Fetches human-readable color name from API
 * - Opens AI Color Magic modal with color details
 * - Passes sourceModel for consistent AI usage in compare mode
 * 
 * ADAPTIVE SIZING:
 * - Individual mode: 24x24 (w-24 h-24), larger gaps
 * - Compare mode: 16x16 (w-16 h-16), smaller gaps
 * 
 * @param {Array} colors - Theme colors
 * @param {String} sourceModel - AI model that generated theme
 * @param {String} style - Display style: 'individual' or 'compare'
 * @param {Function} setColorModal - State setter for color modal
 * @param {Component} AsyncColorName - Component for async color name display
 * @returns {JSX.Element|null} Color orbs grid or null if no colors
 */
export const renderColorOrbs = (colors, sourceModel, style, setColorModal, AsyncColorName) => {
  // Validation: return null if no colors to display
  if (!colors || colors.length === 0) return null;
  
  // Parse colors to array format
  const parsedColors = parseColors(colors);
  
  // ========================================================================
  // ADAPTIVE SIZING BASED ON DISPLAY MODE
  // ========================================================================
  const orbSize = style === 'individual' ? 'w-24 h-24' : 'w-16 h-16';
  const gap = style === 'individual' ? 'gap-8' : 'gap-4';
  const containerMargin = style === 'individual' ? 'mb-12' : 'mb-8';
  const shineSize = style === 'individual' 
    ? { large: 'w-6 h-6', small: 'w-4 h-4' }    // Individual: larger shine effects
    : { large: 'w-4 h-4', small: 'w-3 h-3' };   // Compare: smaller shine effects
  
  return (
    <div className={`flex flex-wrap justify-center ${gap} ${containerMargin}`}>
      {parsedColors.map((color, index) => (
        <div 
          key={index} 
          className="group/orb transform hover:scale-125 transition-all duration-700 cursor-pointer"
          onClick={async () => {
            // ================================================================
            // CLICK HANDLER: OPEN AI COLOR MAGIC MODAL
            // ================================================================
            const selectedColor = extractHexFromColor(color);  // Extract hex code
            const colorName = await getColorName(selectedColor);  // Fetch human-readable name
            
            // Open modal with color details
            setColorModal({
              show: true,
              color: selectedColor,
              position: 50,                           // Center position in color flow
              colorName: colorName,
              applications: ['SHOW_AI_BUTTON'],       // Show "AI Color Magic" button
              selectedCategory: 'decor',              // Default category
              sourceModel: sourceModel                // Pass model for consistent AI usage
            });
          }}
        >
          <div className="relative">
            {/* ============================================================
                OUTER GLOW RING - Animated ping effect
                ============================================================ */}
            <div 
              className="absolute inset-0 rounded-full animate-ping opacity-30"
              style={{ 
                backgroundColor: extractHexFromColor(color),
                transform: 'scale(1.4)',
                animationDuration: '3s',
                animationDelay: `${index * 0.5}s`    // Stagger animation by index
              }}
            ></div>
            
            {/* ============================================================
                MAIN COLOR ORB - 3D appearance with shadow
                ============================================================ */}
            <div 
              className={`${orbSize} rounded-full shadow-2xl border-4 border-white relative overflow-hidden group-hover/orb:rotate-12 transition-all duration-700`}
              style={{ 
                backgroundColor: extractHexFromColor(color),
                // Double shadow: outer glow + inner highlight
                boxShadow: `0 15px 40px ${extractHexFromColor(color)}60, inset 0 0 20px rgba(255,255,255,0.3)`
              }}
            >
              {/* Inner shine effect - creates 3D appearance */}
              <div className={`absolute top-2 left-2 ${shineSize.large} bg-white opacity-40 rounded-full blur-sm`}></div>
              <div className={`absolute bottom-2 right-2 ${shineSize.small} bg-black opacity-20 rounded-full blur-sm`}></div>
            </div>
            
            {/* ============================================================
                FLOATING COLOR NAME - Below orb
                ============================================================ */}
            <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
              <div 
                className="px-4 py-2 rounded-full shadow-lg border-2 border-white font-bold text-sm capitalize transform group-hover/orb:scale-110 transition-all duration-300"
                style={{ 
                  backgroundColor: extractHexFromColor(color),
                  color: getContrastColor(extractHexFromColor(color)),
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                <AsyncColorName 
                  color={color} 
                  extractHexFromColor={extractHexFromColor}
                  getColorName={getColorName}
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// COLOR CODES RENDERER (Hex Code Display)
// ============================================================================

/**
 * Render professional hex code display
 * 
 * WHAT IT DOES:
 * 1. Displays hex codes in elegant format
 * 2. Shows color swatch next to each code
 * 3. Uses monospace font for codes
 * 4. Ensures text contrast on colored backgrounds
 * 5. Adds hover effects (scale, shine animation)
 * 
 * VISUAL DESIGN:
 * - Container: Gradient gray background with border
 * - Title: "Professional Color Codes" with underline
 * - Each code: Color swatch + hex code in pill shape
 * - Hover: Scale up, shine animation
 * 
 * ADAPTIVE SIZING:
 * - Individual mode: Larger text, bigger swatches, more padding
 * - Compare mode: Compact for side-by-side display
 * 
 * ACCESSIBILITY:
 * - Uses getContrastColor() to ensure readable text
 * - Monospace font for hex codes (easier to read)
 * - Drop shadow for better legibility
 * 
 * @param {Array} colors - Theme colors
 * @param {String} style - Display style: 'individual' or 'compare'
 * @returns {JSX.Element|null} Hex code display or null if no colors
 */
export const renderColorCodes = (colors, style = 'compare') => {
  // Validation: return null if no colors to display
  if (!colors || colors.length === 0) return null;
  
  // Parse colors to array format
  const parsedColors = parseColors(colors);
  
  // ========================================================================
  // ADAPTIVE SIZING BASED ON DISPLAY MODE
  // ========================================================================
  const containerClass = style === 'individual' 
    ? 'mt-16 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl border-2 border-gray-200 shadow-xl'
    : 'mt-16 p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-gray-200 shadow-lg group';
  const titleClass = style === 'individual' ? 'text-2xl' : 'text-lg';
  const codeSize = style === 'individual' ? 'w-5 h-5' : 'w-3 h-3';        // Color swatch size
  const textSize = style === 'individual' ? 'text-sm' : 'text-xs';        // Hex code text size
  const underlineWidth = style === 'individual' ? 'w-24' : 'w-16';        // Title underline width
  const gapSize = style === 'individual' ? 'gap-3' : 'gap-2';             // Gap between codes
  const paddingSize = style === 'individual' ? 'px-4 py-3' : 'px-3 py-2'; // Code pill padding
  const roundingSize = style === 'individual' ? 'rounded-xl' : 'rounded-lg'; // Code pill rounding
  
  return (
    <div className={containerClass}>
      {/* Section title with decorative underline */}
      <div className="text-center mb-4">
        <h5 className={`${titleClass} font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gray-700 group-hover:to-gray-800 group-hover:bg-clip-text transition-all duration-300 mb-2`}>
          Professional Color Codes
        </h5>
        <div className={`${underlineWidth} h-0.5 bg-gradient-to-r from-gray-500 to-gray-600 mx-auto rounded-full`}></div>
      </div>
      
      {/* Hex codes grid */}
      <div className={`flex flex-wrap justify-center ${gapSize}`}>
        {parsedColors.map((color, index) => (
          <div key={index} className="group/hex transform hover:scale-105 transition-all duration-300">
            <div 
              className={`flex items-center ${paddingSize} ${roundingSize} shadow-lg border-2 border-white hover:shadow-xl transition-all duration-300 relative overflow-hidden`}
              style={{ backgroundColor: extractHexFromColor(color) }}
            >
              {/* Shine effect on hover - animated gradient sweep */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover/hex:opacity-20 group-hover/hex:animate-pulse"></div>
              
              {/* Code content */}
              <div className="relative z-10 flex items-center">
                {/* Color swatch circle */}
                <div 
                  className={`${codeSize} rounded-full mr-2 border-2 border-white shadow-sm`}
                  style={{ backgroundColor: extractHexFromColor(color) }}
                ></div>
                
                {/* Hex code text */}
                <span 
                  className={`${textSize} font-mono font-bold drop-shadow-lg`}
                  style={{ color: getContrastColor(extractHexFromColor(color)) }}  // Ensure readable contrast
                >
                  {extractHexFromColor(color)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// COLOR FLOW RENDERER (Interactive Gradient Bar)
// ============================================================================

/**
 * Render interactive color flow gradient
 * 
 * WHAT IT DOES:
 * 1. Creates smooth gradient bar from all theme colors
 * 2. Allows clicking anywhere on gradient
 * 3. Calculates clicked color based on position
 * 4. Opens AI Color Magic modal with selected color
 * 5. Shows visual feedback on hover (brightness increase)
 * 
 * INTERACTION:
 * - Click anywhere on gradient bar
 * - Calculates percentage position (0-100%)
 * - Determines which color at that position
 * - Fetches color name from API
 * - Opens modal with color details
 * 
 * COLOR CALCULATION:
 * - 0%: First color
 * - 100%: Last color
 * - Middle: Interpolates between colors based on position
 * - Example: 50% with 3 colors → middle color
 * 
 * VISUAL DESIGN:
 * - Title: "Color Flow Visualization"
 * - Instruction: "Click anywhere on the gradient..."
 * - Gradient bar: 20px height, rounded, shadow
 * - Hover: Brightness increase, larger shadow
 * 
 * USAGE:
 * - Individual mode only (not shown in compare mode)
 * - Provides immersive color exploration experience
 * 
 * @param {Array} colors - Theme colors
 * @param {String} sourceModel - AI model that generated theme
 * @param {Function} setColorModal - State setter for color modal
 * @returns {JSX.Element|null} Color flow gradient or null if no colors
 */
export const renderColorFlow = (colors, sourceModel, setColorModal) => {
  // Validation: return null if no colors to display
  if (!colors || colors.length === 0) return null;
  
  // Parse colors to array format
  const parsedColors = parseColors(colors);
  
  return (
    <div className="mt-8 mb-8">
      {/* Section title and instructions */}
      <div className="text-center mb-8">
        <h4 className="text-2xl font-serif font-bold text-gray-800 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-indigo-600 group-hover:bg-clip-text transition-all duration-300 mb-2">
          Color Flow Visualization
        </h4>
        <p className="text-sm text-gray-600 mb-4">Click anywhere on the gradient to explore color details</p>
        <div className="w-24 h-0.5 bg-gradient-to-r from-purple-400 to-indigo-500 mx-auto rounded-full"></div>
      </div>
      
      {/* Interactive gradient bar */}
      <div 
        className="relative h-20 rounded-3xl overflow-hidden shadow-2xl border-4 border-white cursor-pointer hover:shadow-3xl transition-all duration-300"
        onClick={async (e) => {
          // ================================================================
          // CLICK HANDLER: CALCULATE COLOR AT CLICK POSITION
          // ================================================================
          
          // Get click position relative to gradient bar
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;                    // X position in pixels
          const percentage = (x / rect.width) * 100;          // Convert to percentage (0-100)
          
          // Determine which color at this position
          let selectedColor;
          if (percentage <= 0) {
            // Clicked at start - use first color
            selectedColor = extractHexFromColor(parsedColors[0]);
          } else if (percentage >= 100) {
            // Clicked at end - use last color
            selectedColor = extractHexFromColor(parsedColors[parsedColors.length - 1]);
          } else {
            // Clicked in middle - calculate which color
            // Example: 50% with 3 colors → index 1 (middle color)
            const colorIndex = Math.floor((percentage / 100) * (parsedColors.length - 1));
            selectedColor = extractHexFromColor(parsedColors[colorIndex]);
          }
          
          // Fetch human-readable color name
          const colorName = await getColorName(selectedColor);
          
          // Open AI Color Magic modal
          setColorModal({
            show: true,
            color: selectedColor,
            position: percentage,                    // Store click position for visual indicator
            colorName: colorName,
            applications: ["SHOW_AI_BUTTON"],        // Show "AI Color Magic" button
            selectedCategory: "decor",               // Default category
            sourceModel: sourceModel                 // Pass model for consistent AI usage
          });
        }}
      >
        {/* Interactive gradient overlay - hover brightens */}
        <div 
          className="absolute inset-0 hover:brightness-110 transition-all duration-300"
          style={{
            // Build smooth gradient from all colors
            // Example: linear-gradient(90deg, #FFB6C1 0%, #FFD700 50%, #E6E6FA 100%)
            background: `linear-gradient(90deg, ${parsedColors.map((color, index) => 
              `${extractHexFromColor(color)} ${(index / (parsedColors.length - 1)) * 100}%`
            ).join(', ')})`
          }}
        ></div>
        
        {/* Flowing overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse pointer-events-none" style={{ animationDuration: '2s' }}></div>
        
        {/* Color dots floating on wave */}
        {parsedColors.map((color, index) => (
          <div 
            key={index}
            className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-lg animate-bounce pointer-events-none"
            style={{ 
              backgroundColor: extractHexFromColor(color),
              left: `${(index / (parsedColors.length - 1)) * 90 + 5}%`,
              animationDelay: `${index * 0.3}s`,
              animationDuration: '2s'
            }}
          ></div>
        ))}
        
        {/* Click hint */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/20 text-white text-xs px-3 py-1 rounded-full opacity-70">
          Click to explore
        </div>
        
        {/* Color dots floating on wave */}
        {parsedColors.map((color, index) => (
          <div 
            key={index}
            className="absolute top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-lg animate-bounce pointer-events-none"
            style={{ 
              backgroundColor: extractHexFromColor(color),
              left: `${(index / (parsedColors.length - 1)) * 90 + 5}%`,
              animationDelay: `${index * 0.3}s`,
              animationDuration: '2s'
            }}
          ></div>
        ))}
        
        {/* Click hint */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black/20 text-white text-xs px-3 py-1 rounded-full opacity-70">
          Click to explore
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COLOR PALETTE RENDERER (Main Orchestrator)
// ============================================================================

/**
 * Render complete color palette section
 * 
 * WHAT IT DOES:
 * 1. Renders section title with animated icon
 * 2. Orchestrates child renderers (orbs, codes, flow)
 * 3. Adapts styling based on display mode
 * 4. Shows color flow only in individual mode
 * 
 * CHILD RENDERERS:
 * - renderColorOrbs: Interactive color circles
 * - renderColorCodes: Hex code display
 * - renderColorFlow: Animated gradient flow (individual mode only)
 * 
 * STYLES:
 * - 'individual': Large title, bigger icons, includes color flow
 * - 'compare': Compact title, smaller icons, no color flow
 * 
 * @param {Array} colors - Theme colors
 * @param {String} sourceModel - AI model that generated theme
 * @param {String} style - Display style: 'individual' or 'compare'
 * @param {Function} renderColorOrbs - Child renderer for color orbs
 * @param {Function} renderColorCodes - Child renderer for hex codes
 * @param {Function} renderColorFlow - Child renderer for gradient flow
 * @returns {JSX.Element|null} Color palette section or null if no colors
 */
export const renderColorPalette = (colors, sourceModel, style, renderColorOrbs, renderColorCodes, renderColorFlow) => {
  // Validation: return null if no colors to display
  if (!colors || colors.length === 0) return null;

  // ========================================================================
  // ADAPTIVE STYLING BASED ON DISPLAY MODE
  // ========================================================================
  // Individual mode: Larger, more prominent
  // Compare mode: Compact for side-by-side display
  const titleClass = style === 'individual' 
    ? 'text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300'
    : 'text-lg font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300';
  const iconSize = style === 'individual' ? 'text-3xl' : 'text-2xl';

  return (
    <div className="group">
      {/* Section title with animated palette icon */}
      <h3 className={titleClass}>
        <span className={`mr-3 ${iconSize} animate-bounce`}>🎨</span> Color Palette
      </h3>
      
      {/* Render child components */}
      {renderColorOrbs(colors, sourceModel, style)}           {/* Interactive color circles */}
      {renderColorCodes(colors, style)}                       {/* Hex code display */}
      {style === 'individual' && renderColorFlow(colors, sourceModel)}  {/* Gradient flow (individual only) */}
    </div>
  );
};

// ============================================================================
// ANIMATED BACKGROUND RENDERER
// ============================================================================

/**
 * Render animated background with theme colors
 * 
 * WHAT IT DOES:
 * 1. Creates subtle animated background using theme colors
 * 2. Applies low opacity (5%) for non-intrusive effect
 * 3. Uses pulse animation for gentle movement
 * 4. Blends all theme colors into gradient
 * 
 * COLOR PROCESSING:
 * - Parses colors from various formats (array/string)
 * - Extracts hex codes from "Name (#HEX)" format
 * - Adds transparency (10) to each color for subtle effect
 * - Creates linear gradient from all colors
 * 
 * USAGE:
 * - Theme cards: Subtle background animation
 * - Modal backgrounds: Immersive color experience
 * 
 * @param {Array|String} colors - Theme colors in various formats
 * @param {String} fallbackColor - Fallback if no colors (default: gray)
 * @returns {JSX.Element} Animated background div
 */
export const renderAnimatedBackground = (colors, fallbackColor = '#E5E7EB10') => {
  return (
    <div className="absolute inset-0 opacity-5">
      {/* Animated gradient background */}
      <div 
        className="absolute inset-0 animate-pulse rounded-3xl"
        style={{
          // Build gradient from theme colors
          // Example: linear-gradient(135deg, #FFB6C110, #FFD70010, #E6E6FA10)
          background: `linear-gradient(135deg, ${
            colors 
              ? parseColors(colors)                    // Parse colors to array
                  .map(color => extractHexFromColor(color) + '10')  // Extract hex + add transparency
                  .join(', ')                          // Join with commas
              : fallbackColor                          // Use fallback if no colors
          })`
        }}
      ></div>
    </div>
  );
};

// ============================================================================
// THEME HEADER RENDERER
// ============================================================================

/**
 * Render theme header with AI model branding
 * 
 * WHAT IT DOES:
 * 1. Displays AI model name and logo
 * 2. Applies model-specific color scheme
 * 3. Shows subtitle (e.g., "Google's Latest AI")
 * 4. Renders decorative gradient line
 * 
 * STYLES:
 * - 'compare': Compact header for side-by-side comparison
 * - 'individual': Large header for single theme display
 * 
 * MODEL BRANDING:
 * - Gemini: Blue/Purple gradient
 * - Claude: Orange gradient
 * - OpenAI: Blue/Green gradient
 * 
 * @param {String} modelName - AI model: 'gemini', 'claude', 'openai'
 * @param {String} logoSrc - Logo image source (optional)
 * @param {String} style - Display style: 'compare' or 'individual'
 * @returns {JSX.Element} Theme header component
 */
export const renderThemeHeader = (modelName, logoSrc, style = 'compare') => {
  /**
   * Get model-specific configuration (colors, titles, gradients)
   * Each AI model has unique branding colors
   */
  const getModelConfig = (model) => {
    switch (model.toLowerCase()) {
      case 'gemini':
        return {
          title: 'Gemini AI',
          subtitle: 'Google\'s Latest AI',
          bgGradient: 'from-blue-100 to-purple-50',      // Background gradient
          textGradient: 'from-blue-600 to-purple-600',   // Text gradient
          lineGradient: 'from-blue-400 to-purple-500'    // Decorative line gradient
        };
      case 'claude':
        return {
          title: 'Claude AI',
          subtitle: 'by Anthropic',
          bgGradient: 'from-orange-100 to-orange-50',
          textGradient: 'from-orange-600 to-orange-700',
          lineGradient: 'from-orange-400 to-orange-500'
        };
      case 'openai':
        return {
          title: 'OpenAI',
          subtitle: 'GPT Model',
          bgGradient: 'from-blue-100 to-green-50',
          textGradient: 'from-blue-600 to-green-600',
          lineGradient: 'from-blue-400 to-green-500'
        };
      default:
        // Fallback for unknown models
        return {
          title: modelName,
          subtitle: 'AI Model',
          bgGradient: 'from-gray-100 to-gray-50',
          textGradient: 'from-gray-600 to-gray-700',
          lineGradient: 'from-gray-400 to-gray-500'
        };
    }
  };

  // Get configuration for selected model
  const config = getModelConfig(modelName);

  // ========================================================================
  // RENDER: INDIVIDUAL STYLE (Large header for single theme)
  // ========================================================================
  if (style === 'individual') {
    // Individual mode - show logo if available, otherwise use generic icon
    return (
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-rose-400 to-pink-400 rounded-full shadow-xl mb-6 animate-bounce">
          {logoSrc ? (
            <img src={logoSrc} alt={config.title} className="w-10 h-10 object-contain" />
          ) : (
            <span className="text-2xl animate-pulse">🎨</span>
          )}
        </div>
        
        <h3 className="text-4xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent mb-4">
          Your Dream Wedding Theme
        </h3>
        
        <div className="w-24 h-1 bg-gradient-to-r from-rose-400 to-pink-500 mx-auto rounded-full mb-6"></div>
      </div>
    );
  } else {
    // Compare mode - model-specific header
    return (
      <div className="text-center mb-8 relative z-10">
        <div className={`inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r ${config.bgGradient} rounded-full shadow-2xl mb-6 border-4 border-white hover:scale-110 transition-all duration-500`}>
          <img src={logoSrc} alt={config.title} className="w-12 h-12 object-contain" />
        </div>
        <h4 className={`text-3xl font-serif font-bold bg-gradient-to-r ${config.textGradient} bg-clip-text text-transparent mb-2`}>
          {config.title}
        </h4>
        <p className="text-sm text-gray-600 font-medium">{config.subtitle}</p>
        <div className={`w-16 h-0.5 bg-gradient-to-r ${config.lineGradient} mx-auto mt-3 rounded-full`}></div>
      </div>
    );
  }
};

/**
 * Renders ritual suggestions section
 * @param {Array} rituals - Array of ritual objects with suggested_rituals
 * @param {string} themeName - Name of the theme for display
 * @param {Function} setRitualModal - Function to open ritual modal
 * @returns {JSX.Element|null} Ritual section JSX or null
 */
export const renderRitualSection = (rituals, themeName, setRitualModal, timeTracker = null, themeId = null) => {
  if (!rituals || rituals.length === 0) return null;

  return (
    <div className="mt-12 transition-all duration-700 delay-600 opacity-100 translate-y-0">
      <div className="relative bg-white/60 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 hover:shadow-3xl transition-all duration-500">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className="text-4xl animate-bounce">🎭</span>
            <h3 className="text-4xl font-serif font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 bg-clip-text text-transparent">
              Suggested Rituals {themeName ? `for ${themeName}` : 'for Your Theme'}
            </h3>
          </div>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            {themeName ? (
              <>Based on your <span className="font-semibold text-purple-600">{themeName}</span> theme, these cultural rituals would beautifully complement your celebration</>
            ) : (
              <>These cultural rituals would beautifully complement your celebration</>
            )}
          </p>
        </div>

        {/* Ritual Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {[...rituals].sort((a, b) => (b.similarity || 0) - (a.similarity || 0)).slice(0, 6).map((ritual, index) => (
            <div 
              key={ritual.id || index}
              onClick={() => setRitualModal({ show: true, ritual })}
              className="group relative bg-gradient-to-br from-white to-purple-50 rounded-2xl p-6 border-2 border-purple-200/50 hover:border-purple-400 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
            >
              {/* Ritual Name */}
              <h4 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors">
                {ritual.name}
              </h4>

              {/* Region and Similarity Score */}
              <div className="flex justify-between items-center gap-3 mb-3">
                <span className="flex items-center justify-center px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-semibold">
                  {ritual.culture_display_name}
                </span>
                {ritual.similarity && (
                  <span className="flex items-center justify-center px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-semibold border border-green-200">
                    {Math.round(ritual.similarity * 100)}% match
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {ritual.description || ritual.meaning?.substring(0, 120) + '...'}
              </p>

              {/* Learn More Button */}
              <div className="text-center">
                <button
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-semibold text-sm hover:shadow-lg transition-all animate-pulse"
                >
                  Learn More
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Explore All Button */}
        <div className="text-center">
          <a 
            href={`/wedcraft-studio/cultural-ritual-explainer?from=theme-page&ref=${encodeURIComponent(themeId || themeName || '')}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={async (e) => {
              // Track ritual navigation intent (once per theme - themeName fallback for unsaved themes)
              const trackKey = `ritual_nav_tracked_${themeId || themeName || 'unknown'}`;
              if (!sessionStorage.getItem(trackKey)) {
                sessionStorage.setItem(trackKey, '1');
                try {
                  await apiService.feedback.trackRitualNavigation({
                    theme_id: themeId || null,
                    ritual_count: rituals?.length || 0,
                    entry_point: 'theme-page'
                  });
                } catch (error) {
                  console.error('Error tracking ritual navigation:', error);
                }
              }
            }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 animate-pulse"
          >
            <span className="text-2xl">🌍</span>
            Explore All Cultural Rituals
            <span className="text-2xl">✨</span>
          </a>
        </div>
      </div>
    </div>
  );
};
